/*
 * Jira Ticket:
 * Created Date: Thu, 4th Jun 2020, 11:28:18 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React from 'react';
import {
  ArrayInput,
  BooleanInput,
  Edit,
  FormDataConsumer,
  NumberInput,
  required,
  SimpleForm,
  TextInput,
  usePermissions,
  Title,
  ReferenceArrayInput,
  SelectArrayInput,
} from 'react-admin';
import { connect } from 'react-redux';
import { InputAdornment, Typography } from '@material-ui/core';
import { CustomSimpleFormIterator, SaveDeleteToolbar } from 'Components';
import { AccessDenied } from 'Layout';
import { isSuperAdmin } from 'utils/roles';
import { isValidCost, nonNegativeInt } from 'utils/validation';
import { columnStyles } from 'styles';

const costValidation = [required(), isValidCost];
const nonNegativeValidation = [required(), nonNegativeInt];

const SanitizedForm = ({ classes }) => {
  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <TextInput
          source="name"
          label="resources.SubscriptionTier.fields.name"
          validate={required()}
        />
        <FormDataConsumer>
          {({ formData }) =>
            !formData.isFreeTier ? (
              <NumberInput
                source="monthlyPlan.cost.amount"
                validate={costValidation}
                parse={x => x} //prevent overwriting 12.00 -> 12
                label="resources.SubscriptionTier.fields.monthlyCost"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">£</InputAdornment>
                  ),
                }}
              />
            ) : null
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData }) =>
            !formData.isFreeTier ? (
              <NumberInput
                source="annualPlan.cost.amount"
                validate={costValidation}
                parse={x => x} //prevent overwriting 12.00 -> 12
                label="resources.SubscriptionTier.fields.annualCost"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">£</InputAdornment>
                  ),
                }}
              />
            ) : null
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData }) =>
            !formData.isFreeTier ? (
              <NumberInput
                source="freeTrialDuration"
                label="resources.SubscriptionTier.fields.freeTrailLength"
                step={1}
                disabled={true} // can't change free trial length once set (PayPal limitation)
                validate={nonNegativeValidation}
              />
            ) : null
          }
        </FormDataConsumer>
        <NumberInput
          source="maximumEmployees"
          label="resources.SubscriptionTier.fields.maxEmployee"
          step={1}
          validate={nonNegativeValidation}
        />
        <NumberInput
          source="maximumSubcontractors"
          label="resources.SubscriptionTier.fields.maxSubcontractor"
          step={1}
          validate={nonNegativeValidation}
        />
        <NumberInput
          source="maximumProjects"
          label="resources.SubscriptionTier.fields.maxProject"
          step={1}
          validate={nonNegativeValidation}
        />
        <FormDataConsumer>
          {({ formData }) =>
            !formData.isInitialTier ? (
              <BooleanInput
                label="resources.SubscriptionTier.fields.isCustomTier"
                source="isCustomTier"
              />
            ) : null
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            const { isCustomTier, isInitialTier } = formData;
            return isCustomTier && !isInitialTier ? (
              <ReferenceArrayInput
                perPage={1000}
                resource="Organisation"
                source="customOrganisationsIds"
                reference="Organisation"
                label="resources.SubscriptionTier.fields.customOrganisations"
                disabled={!isCustomTier}
              >
                <SelectArrayInput optionText="name" />
              </ReferenceArrayInput>
            ) : null;
          }}
        </FormDataConsumer>
      </div>
      <div className={classes.permissionColumn}>
        <Typography variant="h6" gutterBottom>
          Permissions
        </Typography>
        <ArrayInput source="capabilities" label="">
          <CustomSimpleFormIterator disableAdd disableRemove>
            <FormDataConsumer>
              {({ getSource, scopedFormData }) => {
                return (
                  <BooleanInput
                    key={scopedFormData.id}
                    source={getSource('enabled')}
                    label={scopedFormData.description}
                  />
                );
              }}
            </FormDataConsumer>
          </CustomSimpleFormIterator>
        </ArrayInput>
      </div>
    </div>
  );
};

const SubscriptionTierEdit = ({ formValues, dispatch, ...props }) => {
  const classes = columnStyles();
  const { permissions } = usePermissions();
  const { isInitialTier } = formValues[props.id] || {};
  return isSuperAdmin(permissions) ? (
    <>
      <Title title={`resources.General.cmsTitle`} />
      <Edit undoable={false} {...props}>
        <SimpleForm
          redirect="list"
          toolbar={<SaveDeleteToolbar hideDelete={isInitialTier} />}
        >
          <SanitizedForm classes={classes} />
        </SimpleForm>
      </Edit>
    </>
  ) : (
    <AccessDenied />
  );
};

const mapStateToProps = state => {
  return {
    formValues:
      state.admin.resources.SubscriptionTier &&
      state.admin.resources.SubscriptionTier.data,
  };
};

export default connect(mapStateToProps)(SubscriptionTierEdit);
